/**
 * Player-facing What's New modal — one unread post at a time (newest first).
 */
import { fetchWhatsNewPosts, type WhatsNewPostRow } from "../lib/cloud";
import { createModalCloseButton } from "./modal-close-button";

const SEEN_STORAGE_KEY = "cgWhatsNewSeenV1";

function readSeenIds(): string[] {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string" && id.length > 0);
  } catch {
    return [];
  }
}

function writeSeenIds(ids: string[]): void {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota */
  }
}

function markSeen(id: string): void {
  const seen = new Set(readSeenIds());
  seen.add(id);
  writeSeenIds([...seen]);
}

function waitForHomeReady(): Promise<void> {
  return new Promise((resolve) => {
    const deadline = Date.now() + 20000;
    const tick = () => {
      const overlay = document.getElementById("accountBootstrapOverlay");
      const home = document.getElementById("startScreen");
      const overlayHidden = !overlay || overlay.classList.contains("hidden");
      const homeVisible = !!home && !home.classList.contains("hidden");
      if ((overlayHidden && homeVisible) || Date.now() > deadline) {
        resolve();
        return;
      }
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

let modalOpen = false;
let queue: WhatsNewPostRow[] = [];

function closeModalLayer(layer: HTMLElement): void {
  layer.remove();
  modalOpen = false;
}

function showPost(post: WhatsNewPostRow, index: number, total: number): void {
  modalOpen = true;

  const layer = document.createElement("div");
  layer.className = "modeDetailModal whatsNewModal screen";
  layer.setAttribute("role", "dialog");
  layer.setAttribute("aria-modal", "true");
  layer.setAttribute("aria-labelledby", "whatsNewModalTitle");

  const panel = document.createElement("div");
  panel.className = "modeDetailModalPanel modeBox whatsNewModalPanel";

  const dismiss = () => {
    markSeen(post.id);
    closeModalLayer(layer);
    void showNextFromQueue(index + 1);
  };

  panel.appendChild(createModalCloseButton(dismiss, "Close"));

  const kicker = document.createElement("p");
  kicker.className = "whatsNewModalKicker";
  kicker.textContent = total > 1 ? `What's New · ${index + 1} of ${total}` : "What's New";
  panel.appendChild(kicker);

  const title = document.createElement("h2");
  title.id = "whatsNewModalTitle";
  title.textContent = post.title;
  panel.appendChild(title);

  const body = document.createElement("div");
  body.className = "whatsNewModalBody";
  body.textContent = post.body;
  panel.appendChild(body);

  const actions = document.createElement("div");
  actions.className = "whatsNewModalActions";
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "btn green";
  okBtn.textContent = total > 1 && index + 1 < total ? "Next" : "Got it";
  okBtn.onclick = dismiss;
  actions.appendChild(okBtn);
  panel.appendChild(actions);

  layer.appendChild(panel);
  layer.addEventListener("click", (ev) => {
    if (ev.target === layer) dismiss();
  });
  document.body.appendChild(layer);
  okBtn.focus();
}

async function showNextFromQueue(fromIndex: number): Promise<void> {
  if (modalOpen) return;
  const unseen = queue.slice(fromIndex);
  if (!unseen.length) return;
  showPost(unseen[0], fromIndex, queue.length);
}

async function runWhatsNewFlow(): Promise<void> {
  if (modalOpen) return;
  await waitForHomeReady();
  const posts = await fetchWhatsNewPosts();
  if (!posts.length) return;

  const seen = new Set(readSeenIds());
  queue = posts.filter((p) => !seen.has(p.id));
  if (!queue.length) return;

  showPost(queue[0], 0, queue.length);
}

/** Call once on app startup (after DOM is ready). */
export function initWhatsNewModal(): void {
  void runWhatsNewFlow();
}

/** Re-check after account bootstrap finishes (e.g. auth refresh). */
export function refreshWhatsNewModal(): void {
  if (modalOpen) return;
  void runWhatsNewFlow();
}
